import { FormEvent, useRef, useState } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import logo from '../../assets/images/hogcloud-logo1.svg';
import printer from '../../assets/images/service-icon-3dprint.webp';
import './index.css';

const classes = ['3D printing Basics L1', '3D Printing L2'];

function Learn() {
  const [token, setToken] = useState('');
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState('');
  const verification = useRef<TurnstileInstance | null>(null);
  const endpoint = process.env.REACT_APP_MAIL_FUNCTION_URL || '';
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sending || submitted) return;
    if (!endpoint || !siteKey) {
      setStatus('Registration is temporarily unavailable. Please email sales@hogcloud.net.');
      return;
    }
    if (!token) {
      setStatus('Please complete the human verification.');
      return;
    }
    const data = new FormData(event.currentTarget);
    const name = String(data.get('studentName') || '').trim();
    if (!name) {
      setStatus('Please enter the student name.');
      return;
    }
    const selectedClasses = data.getAll('classInterest');
    if (!selectedClasses.length) {
      setStatus('Please select at least one class.');
      return;
    }
    setSending(true);
    setStatus('Sending registration…');
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullname: name,
          email: String(data.get('email') || '').trim(),
          requested_services: ['tutoring'],
          custom_message: `Hogcloud Learning registration\nStudent Name: ${name}\nStudent Age: ${data.get('studentAge')}\nClasses you are interested in: ${selectedClasses.join(', ')}`,
          turnstile_token: token,
        }),
      });
      if (!response.ok) throw new Error('Registration failed');
      setSubmitted(true);
      setStatus('Registration request received! We’ll email you to confirm availability and class details.');
    } catch {
      setStatus('Unable to send your registration. Please try again or email sales@hogcloud.net.');
      setToken('');
      verification.current?.reset();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="learn-page">
      <header className="learn-hero">
        <img src={logo} alt="Hogcloud logo" width="240" />
        <div className="learn-hero-copy">
          <p className="learn-eyebrow">Learn. Design. Create.</p>
          <h1>Hogcloud Learning</h1>
          <p>Explore 3D printing and bring your ideas to life, one class at a time.</p>
        </div>
      </header>
      <section aria-labelledby="learn-classes-heading">
        <div className="learn-category">
          <img src={printer} alt="3D printer" width="64" height="64" />
          <h2 id="learn-classes-heading">3D printing</h2>
        </div>
        <div className="learn-cards">
          <article className="learn-card">
            <span className="learn-eyebrow">Class 1 · Beginner</span>
            <h3>3D printing Basics L1</h3>
            <dl>
              <div><dt>Location</dt><dd>Fayetteville</dd></div>
              <div><dt>Class size</dt><dd>Limited to 5 students, ages 10+</dd></div>
              <div><dt>Schedule</dt><dd>October 24, November 7 and 11 at 2 p.m.</dd></div>
              <div><dt>Cost</dt><dd>$50</dd></div>
            </dl>
            <p>Learn the basics of 3D printing, where to download your own models, and how to use online modeling tools to design your own 3D prints. No previous knowledge is required.</p>
            <p>By the end of the class, you’ll know how to export your designs and prepare them for print!</p>
            <p>There will be no actual 3D printing in class. A brief visit to the 3D printing facilities at the Fayetteville Public Library will be part of the final class.</p>
          </article>
          <article className="learn-card">
            <span className="learn-eyebrow">Coming soon</span>
            <h3>3D Printing L2</h3>
            <p><strong>Dates TBD</strong></p>
            <p>Interested in Level 2? Submit your interest below and we’ll contact you when class details are available.</p>
          </article>
        </div>
      </section>
      <section className="learn-registration" aria-labelledby="learn-register-heading">
        <h2 id="learn-register-heading">Register your interest</h2>
        <p>Send a registration request to our team. We’ll follow up by email to confirm the details.</p>
        {!submitted && <form onSubmit={handleSubmit} className="learn-form">
          <fieldset disabled={sending}>
            <legend className="learn-sr-only">Student details</legend>
            <div className="learn-fields">
              <label htmlFor="learn-name">Student Name<input id="learn-name" name="studentName" required maxLength={100} autoComplete="name" /></label>
              <label htmlFor="learn-age">Student Age<input id="learn-age" name="studentAge" type="number" required min={10} step={1} /></label>
              <label htmlFor="learn-email">Email (parent or guardian email for minors)<input id="learn-email" name="email" type="email" required maxLength={254} autoComplete="email" /></label>
            </div>
            <fieldset className="learn-options">
              <legend>Classes you are interested in</legend>
              {classes.map((name, index) => <label key={name} htmlFor={`learn-class-${index}`}><input id={`learn-class-${index}`} type="checkbox" name="classInterest" value={name} />{name}</label>)}
            </fieldset>
            {siteKey && endpoint ? <Turnstile ref={verification} siteKey={siteKey} options={{ action: 'contact', size: 'flexible', responseField: false }} onSuccess={setToken} onExpire={() => { setToken(''); setStatus('Verification expired. Please verify again.'); }} onError={() => { setToken(''); setStatus('Verification could not load. Please try again.'); }} /> : <p>Registration is temporarily unavailable. Email <a href="mailto:sales@hogcloud.net">sales@hogcloud.net</a> to register.</p>}
            <button type="submit" disabled={sending || !token || !endpoint}>{sending ? 'Sending…' : 'Send registration request'}</button>
          </fieldset>
        </form>}
        <p role="status" aria-live="polite">{status}</p>
      </section>
    </div>
  );
}

export default Learn;
